import { useRef, useState, forwardRef } from 'react';
import type { OverrideProps } from 'styletron-react';
import useMergedRef from '@react-hook/merged-ref';
import { useStyletron, IconAdd, IconTrash } from '@olxeu-jobs/ui';
import { FormattedMessage } from '~i18n';
import { convertFileToImage } from '~utils/file';
import { useMedia } from '../../hooks';
import { SrText } from '../sr-text';
import { Wrapper, Input, SelectButton, ToolWrapper, ToolButton, Image } from './styled-components';

type InputProps = React.DetailedHTMLProps<
  React.InputHTMLAttributes<HTMLInputElement>,
  HTMLInputElement
>;

export type Props = Omit<
  OverrideProps<'div', InputProps> & {
    defaultImage?: React.ReactElement;
    onImgLoad?: React.ReactEventHandler<HTMLImageElement>;
    onDelete: () => void;
  },
  'ref'
>;

export const LegacyImagePicker = forwardRef<HTMLInputElement, Props>(
  (
    { defaultImage, onImgLoad, onChange, onDelete, accept, $style, className, disabled, ...props },
    ref,
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [image, setImage] = useState<React.ReactNode>(defaultImage);
    const isMd = useMedia('md');
    const [, theme] = useStyletron();
    const mergedRef = useMergedRef(ref, inputRef);

    const handleOnClick = () => {
      inputRef.current?.click();
    };

    const handleOnChange: React.ChangeEventHandler<HTMLInputElement> = async (event) => {
      const [selectedFile] = event.currentTarget.files ?? [];

      onChange?.(event);

      if (!selectedFile) {
        return;
      }

      try {
        const img = await convertFileToImage(selectedFile);

        setImage(<Image src={img.src} alt="" onLoad={onImgLoad} />);
      } catch {
        // do nothing
      }
    };

    const handleOnDeleteClick = () => {
      onDelete();
      setImage(undefined);
    };

    return (
      <Wrapper className={className} $style={$style} data-testid="image-picker">
        {image ? (
          <>
            <ToolWrapper>
              <ToolButton
                type="button"
                onClick={handleOnDeleteClick}
                data-testid="image-picker-delete"
              >
                <IconTrash size={isMd ? undefined : 16} />
                <SrText>
                  <FormattedMessage
                    id="comp.button.remove_this_image"
                    defaultMessage="Delete this image"
                  />
                </SrText>
              </ToolButton>
            </ToolWrapper>
            {image}
          </>
        ) : (
          <SelectButton
            disabled={disabled}
            type="button"
            onClick={handleOnClick}
            data-testid="image-picker-trigger"
          >
            <IconAdd color={theme.colors['text-global-inverse']} />
            <SrText>
              <FormattedMessage id="comp.image_picker.hint" defaultMessage="Click to upload" />
            </SrText>
          </SelectButton>
        )}
        <Input
          data-testid="image-picker-input"
          type="file"
          ref={mergedRef}
          onChange={handleOnChange}
          accept={accept || 'image/*'}
          disabled={disabled}
          {...props}
        />
      </Wrapper>
    );
  },
);
